import React from "react";
import { Grid, Button, Typography, makeStyles } from "@material-ui/core";
import { Box, Container } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNewTwoToneIcon from "@mui/icons-material/ArrowBackIosNewTwoTone";
import { connect, useSelector } from "react-redux";
import { hexToCSSFilter } from "hex-to-css-filter";
import Navbar from "../components/Navbar";
import WebFooter from "../components/WebFooter";
import abtHero from "../assets/web/abt-us/abt-hero.png";
import storyImage from "../assets/web/abt-us/story-image.png";
import freshIngIcon from "../assets/web/abt-us/fresh-ing.svg";
import fastDeliveryIcon from "../assets/web/abt-us/fast-delivery.svg";
import recipeIcon from "../assets/web/abt-us/recipe.svg";

const useStyles = makeStyles(() => ({
  Typography: {
    padding: "1rem",
    textAlign: "justify",
    lineHeight: "normal",
  },
}));

function Aboutus(props) {
  const classes = useStyles();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 900px)");

  const reduxStoreInfo = useSelector((state) => state.sessionReducer?.store_details);
  const storeInfoConfig = useSelector((state) => state.sessionReducer?.store_config);
  const primaryColor = storeInfoConfig?.general_config?.primary_color || reduxStoreInfo?.primary_color || "#eb5c24";
  const iconFilter = primaryColor
    ? `brightness(0) saturate(100%) ${hexToCSSFilter(primaryColor)?.filter || ""}`
    : "brightness(0) saturate(100%) invert(42%) sepia(85%) saturate(1478%) hue-rotate(345deg) brightness(97%) contrast(92%)";
  const badgeBgColor = primaryColor ? `${primaryColor}15` : "#FFF2EC";

  const [storeInfo, setStoreInfo] = React.useState("");

  React.useEffect(() => {
    let StoreConfig = localStorage.getItem("jwt");
    let config = StoreConfig ? JSON.parse(StoreConfig) : null;
    setStoreInfo(reduxStoreInfo || config?.store || {});
  }, [reduxStoreInfo]);

  React.useEffect(() => {
    if (isDesktop) {
      window.scrollTo(0, 0);
    }
  }, [isDesktop]);

  const activeStoreName = storeInfo?.store_name || reduxStoreInfo?.store_name || "The Spice Garden";
  const activeOrgName = storeInfo?.store_org_name || reduxStoreInfo?.store_org_name || activeStoreName;

  if (isDesktop) {
    return (
      <Box sx={{ minHeight: "100vh", backgroundColor: "#ffffff", display: "flex", flexDirection: "column" }}>
        {/* Web Header */}
        <Navbar />

        {/* Hero Banner Section */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: { md: "260px", lg: "300px" },
            backgroundImage: `url(${abtHero})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#ffffff",
            textAlign: "center",
            px: 2,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.65)",
              zIndex: 1,
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 2, maxWidth: "800px" }}>
            <Typography
              variant="h3"
              style={{ fontWeight: 900, fontFamily: "Satoshi, sans-serif", letterSpacing: "-0.5px", marginBottom: "12px" }}
              sx={{ fontSize: { md: "2.5rem", lg: "3rem" } }}
            >
              About Us
            </Typography>
            <Typography
              variant="subtitle1"
              style={{ fontWeight: 600, lineHeight: 1.75, fontFamily: "Satoshi, sans-serif", opacity: 0.9 }}
              sx={{ fontSize: { md: "1.05rem", lg: "1.15rem" } }}
            >
              Bringing authentic spices and flavors to your doorstep since 2015
            </Typography>
          </Box>
        </Box>

        {/* Our Story Section */}
        <Container maxWidth="lg" sx={{ py: { md: 8, lg: 10 } }}>
          <style>{`
            .aboutus-story-img {
              max-width: 100% !important;
              width: 100% !important;
            }
          `}</style>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              gap: { md: 6, lg: 8 },
              paddingLeft: "0px !important",
            }}
          >
            {/* Story Image Container */}
            <Box
              sx={{
                flex: "1 1 50%",
                maxWidth: { md: "500px", lg: "540px" },
                width: "100%",
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#ffffff",
              }}
            >
              <img
                className="aboutus-story-img"
                src={storyImage}
                alt="Our Chef Story"
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  height: "auto",
                  maxHeight: "420px",
                  objectFit: "cover",
                  borderRadius: "20px",
                  display: "block",
                }}
              />
            </Box>

            {/* Story Content */}
            <Box sx={{ flex: "1 1 50%", textAlign: "left" }}>
              <Typography
                variant="subtitle2"
                style={{
                  color: primaryColor,
                  fontWeight: 700,
                  lineHeight: 2.235,
                  fontSize: "16px",
                  fontFamily: "Satoshi, sans-serif",
                  textAlign: "left",
                  marginBottom: "8px",
                }}
              >
                Our Heritage
              </Typography>
              <Typography
                variant="h4"
                style={{ fontWeight: 800, textAlign: "left", fontFamily: "Satoshi, sans-serif", color: "#111827", marginBottom: "20px" }}
                sx={{ fontSize: { md: "2.2rem", lg: "2.5rem" } }}
              >
                Our Story
              </Typography>
              <Typography
                variant="body1"
                style={{ textAlign: "left", color: "#4b5563", fontSize: "1rem", lineHeight: 1.7, fontFamily: "Satoshi, sans-serif", marginBottom: "16px" }}
              >
                {activeStoreName} was born out of a simple, passionate dream: to preserve and share the exquisite art of slow-cooked authentic cuisine. Our founders, inspired by generational family recipes, set out to bridge the gap between traditional spice craft and modern convenience.
              </Typography>
              <Typography
                variant="body1"
                style={{ textAlign: "left", color: "#4b5563", fontSize: "1rem", lineHeight: 1.7, fontFamily: "Satoshi, sans-serif" }}
              >
                We responsibly source every single cardamom pod, chili, and herb directly from local farmers who care as much about flavor integrity as we do. When you order from {activeStoreName}, you aren't just ordering food—you are experiencing a culinary lineage crafted with respect, time, and absolute dedication to the perfect blend.
              </Typography>
            </Box>
          </Box>
        </Container>

        {/* Our Core Values Section */}
        <Box sx={{ backgroundColor: "#f9fafb", py: { md: 8, lg: 10 }, borderTop: "1px solid #f3f4f6", borderBottom: "1px solid #f3f4f6" }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h4"
                style={{ fontWeight: 800, fontFamily: "Satoshi, sans-serif", color: "#111827", marginBottom: "12px" }}
                sx={{ fontSize: { md: "2.2rem", lg: "2.5rem" } }}
              >
                Our Core Values
              </Typography>
              <Typography
                variant="body1"
                style={{ color: "#6b7280", fontSize: "1.05rem", fontWeight: 600, fontFamily: "Satoshi, sans-serif", maxWidth: "650px", margin: "0 auto" }}
              >
                The principles that guide our kitchen, our delivery partners, and our community every single day.
              </Typography>
            </Box>

            {/* 3 Core Value Cards */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { md: "repeat(3, 1fr)" },
                gap: 4,
              }}
            >
              {/* Card 1 */}
              <Box
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  padding: "32px",
                  border: "1px solid rgba(234, 230, 225, 1)",
                  boxShadow: "0px 4px 16px 0px rgba(30, 27, 24, 0.0392)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  textAlign: "left",
                }}
              >
                <Box
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "50%",
                    backgroundColor: badgeBgColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Box component="img" src={freshIngIcon} alt="Fresh Ingredients" style={{ width: "32px", height: "32px", objectFit: "contain", filter: iconFilter }} />
                </Box>
                <Typography
                  variant="h6"
                  style={{ fontWeight: 700, lineHeight: 2.6, textAlign: "left", fontSize: "1.2rem", fontFamily: "Satoshi, sans-serif", color: "#111827", marginBottom: "8px" }}
                >
                  Fresh Ingredients
                </Typography>
                <Typography
                  variant="body2"
                  style={{ textAlign: "left", color: "#6b7280", fontSize: "0.95rem", lineHeight: 1.6, fontFamily: "Satoshi, sans-serif" }}
                >
                  We strictly source organic spices, hand-selected seasonal vegetables, and premium proteins. No shortcuts, no compromises, ever.
                </Typography>
              </Box>

              {/* Card 2 */}
              <Box
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  padding: "32px",
                  border: "1px solid rgba(234, 230, 225, 1)",
                  boxShadow: "0px 4px 16px 0px rgba(30, 27, 24, 0.0392)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  textAlign: "left",
                }}
              >
                <Box
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "50%",
                    backgroundColor: badgeBgColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Box component="img" src={fastDeliveryIcon} alt="Fast Delivery" style={{ width: "32px", height: "32px", objectFit: "contain", filter: iconFilter }} />
                </Box>
                <Typography
                  variant="h6"
                  style={{ fontWeight: 700, lineHeight: 2.6, textAlign: "left", fontSize: "1.2rem", fontFamily: "Satoshi, sans-serif", color: "#111827", marginBottom: "8px" }}
                >
                  Fast Delivery
                </Typography>
                <Typography
                  variant="body2"
                  style={{ textAlign: "left", color: "#6b7280", fontSize: "0.95rem", lineHeight: 1.6, fontFamily: "Satoshi, sans-serif" }}
                >
                  Our specialized thermal-insulated dispatch system ensures your authentic meals arrive at your door piping hot and perfectly fresh.
                </Typography>
              </Box>

              {/* Card 3 */}
              <Box
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  padding: "32px",
                  border: "1px solid rgba(234, 230, 225, 1)",
                  boxShadow: "0px 4px 16px 0px rgba(30, 27, 24, 0.0392)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  textAlign: "left",
                }}
              >
                <Box
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "50%",
                    backgroundColor: badgeBgColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "16px",
                  }}
                >
                  <Box component="img" src={recipeIcon} alt="Authentic Recipes" style={{ width: "32px", height: "32px", objectFit: "contain", filter: iconFilter }} />
                </Box>
                <Typography
                  variant="h6"
                  style={{ fontWeight: 700, lineHeight: 2.6, textAlign: "left", fontSize: "1.2rem", fontFamily: "Satoshi, sans-serif", color: "#111827", marginBottom: "8px" }}
                >
                  Authentic Recipes
                </Typography>
                <Typography
                  variant="body2"
                  style={{ textAlign: "left", color: "#6b7280", fontSize: "0.95rem", lineHeight: 1.6, fontFamily: "Satoshi, sans-serif" }}
                >
                  Every dish is prepared using time-honored slow-cooking methods and strict spice-ratio guides passed down through master chefs.
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Web Footer */}
        <Box sx={{ mt: "auto" }}>
          <WebFooter storeInfo={reduxStoreInfo || storeInfo} primaryColor={primaryColor} />
        </Box>
      </Box>
    );
  }

  // Mobile View (Preserved completely)
  return (
    <Grid style={{ background: "#f1f1f1", height: "100vh" }}>
      <Grid
        style={{
          padding: "0.8rem",
          boxShadow: "0px 0px 9px",
          background: "white",
        }}
      >
        <Button
          onClick={() => {
            navigate("/");
          }}
          style={{
            position: "absolute",
            left: 0,
          }}
        >
          <ArrowBackIosNewTwoToneIcon />
        </Button>
        <Typography variant="h5" style={{ fontWeight: "bold" }}>
          About Us
        </Typography>
      </Grid>

      <Grid style={{ padding: "1rem", marginTop: "1rem", maxHeight: "85vh", overflow: "scroll", scrollbarWidth: "none" }}>
        <Typography className={classes.Typography} style={{ paddingBottom: "0rem" }}>
          At {activeOrgName}, we bring the authentic flavors of South India to the heart of the North, serving up a delectable array of dosas and more in a setting that embodies both hygiene and modern convenience.
        </Typography>

        <Typography className={classes.Typography} style={{ paddingBottom: "0rem" }}>
          We provide state-of-the-art customer experience via Self-Service Kiosk and QR Code SCAN.ORDER.PAY solution designed to provide intuitive and efficient checkout experiences, streamlining operations and improving customer satisfaction.
        </Typography>
      </Grid>
    </Grid>
  );
}

const mapStateToProps = (state) => {
  return {
    reducer: state.customerReducer,
    sessionReducer: state.sessionReducer,
  };
};

export default connect(mapStateToProps)(Aboutus);


